import React,{useEffect,useMemo,useState} from 'react';
import api from '../api';
import {errorText,sourceTime} from './PersonalDashboard';
import FlordvorDashboard from './FlordvorDashboard';
import FlordvorReport from '../components/FlordvorReport';
import DashboardRebuildControls,{SectionRebuildFreshness} from '../components/DashboardRebuildControls';

const show = value => value === null || value === undefined || value === '' ? '—'
  : typeof value === 'number' ? value.toLocaleString('ru-RU',{maximumFractionDigits:2}) : String(value);

// Join projections only after the server has authorised each section and only
// within one published generation. Never fetch the owner's full source JSON.
function generation(envelope){return [envelope?.source_fact_at||'',envelope?.published_at||''].join('|');}
function mergeRows(left,right){
  if(right===undefined||right===null)return left??right;
  if(Array.isArray(right)){
    if(!Array.isArray(left))return right;
    const identity=row=>row?.tag??row?.id??row?.key??row?.name??row?.date;
    if(!right.every(row=>row&&typeof row==='object'&&identity(row)!==undefined))return right;
    const result=left.map(row=>({...row}));
    for(const row of right){const index=result.findIndex(item=>identity(item)===identity(row));if(index<0)result.push(row);else result[index]=mergeRows(result[index],row);}
    return result;
  }
  if(typeof right==='object'){
    const result=left&&typeof left==='object'&&!Array.isArray(left)?{...left}:{};
    for(const [key,value] of Object.entries(right))result[key]=mergeRows(result[key],value);
    return result;
  }
  return right;
}
function mergePublishedPeriods(envelopes,selected,field){
  const own=envelopes[selected];if(!own)return [];
  const values=new Map();
  const matching=Object.entries(envelopes).filter(([,entry])=>generation(entry)===generation(own));
  for(const [,entry] of matching)for(const period of entry.data?.[field]||[]){
    if(typeof period.key==='string')values.set(period.key,mergeRows(values.get(period.key),period));
  }
  const order=own.data?.[field]||matching.find(([,entry])=>entry.data?.[field]?.length)?.[1].data?.[field]||[];
  return order.map(period=>values.get(period.key));
}
function calendarDate(value){
  if(typeof value!=='string'||!/^\d{4}-\d{2}-\d{2}$/.test(value))return '—';
  return new Date(value+'T12:00:00Z').toLocaleDateString('ru-RU',{timeZone:'UTC'});
}
function periodRange(from,to){return from&&to?`${calendarDate(from)}–${calendarDate(to)}`:'—';}
function unfinished(period,mode){
  if(!period?.to)return false;
  if(period.calendarTo)return period.to<period.calendarTo;
  if(mode==='month'&&/^\d{4}-\d{2}-\d{2}$/.test(period.to)){
    const [year,month,day]=period.to.split('-').map(Number);return day<new Date(Date.UTC(year,month,0)).getUTCDate();
  }
  return false;
}
function reportTabs(sections){
  const keys=new Set(sections.map(section=>section.key));
  const labels={overview:'Сводка',wholesale:'Опт · группы',retail:'Розница',preorders:'Предзаказы',dynamics:'Динамика поставки'};
  return sections.filter(section=>!(section.key==='writeoffs'&&keys.has('receipts'))
    &&!(section.key==='profitability'&&keys.has('overview'))
    &&!(section.key==='methodology'&&sections.length>1))
    .map(section=>({...section,label:section.key==='receipts'&&keys.has('writeoffs')?'Списания и приёмки':labels[section.key]||section.label}));
}
const initialStages={
  preflight:'Проверка источника',full_collection:'Получение исходных документов',
  incremental_collection:'Догрузка изменений',financial_verification:'Финансовая сверка',
  awaiting_publication:'Ожидание публикации проверенного снимка',completed:'Первоначальная загрузка завершена',
  checking_resume:'Проверка сохранённого прогресса',collecting:'Получение исходных документов',
  incremental:'Догрузка изменений',financial_reconciliation:'Финансовая сверка',
  awaiting_binding:'Ожидание привязки компании',
};
const initialStates={not_started:'Загрузка ещё не начата',queued:'Загрузка ожидает запуска',
  running:'Загрузка выполняется на сервере',failed:'Загрузка завершилась с ошибкой',
  initial_loading:'Загрузка выполняется на сервере',awaiting_binding:'Ожидается привязка компании',
  interrupted:'Загрузка прервана',completed:'Загрузка завершена'};
function InitialLoadStatus({status,error}){
  const completed=status?.state==='completed'&&status.verified_snapshot_available===true&&!status.initial_loading&&!status.error&&!error;
  if(completed)return <section className="workspace-source" aria-label="Состояние проверенного снимка"><p role="status">{status.previous_snapshot?.origin==='import'
    ?'Импортирован проверенный снимок исходной панели. Импорт не означает обновление источника.'
    :'Проверенный снимок опубликован.'}</p></section>;
  return <section className="workspace-card" aria-labelledby="general-initial-load-heading">
    <h2 id="general-initial-load-heading">Первоначальная загрузка данных</h2>
    {!status&&!error&&<p role="status">Проверяем состояние загрузки…</p>}
    {status&&<>
      <p role="status">{initialStates[status.state]||'Состояние первоначальной загрузки пока не получено.'}</p>
      <dl className="workspace-facts">
        <div><dt>Этап</dt><dd>{initialStages[status.stage]||'—'}</dd></div>
        <div><dt>Получено страниц</dt><dd>{show(status.counters?.received_pages)}</dd></div>
        <div><dt>Успешных обращений</dt><dd>{show(status.counters?.completed_calls)}</dd></div>
        <div><dt>Получено карточек документов</dt><dd>{show(status.counters?.unique_document_details_received)}</dd></div>
      </dl>
      <dl className="workspace-facts">
        <div><dt>Последнее успешное действие</dt><dd>{sourceTime(status.last_successful_action_at)}</dd></div>
        <div><dt>Состояние получено</dt><dd>{sourceTime(status.observed_at)}</dd></div>
      </dl>
      {(status.stale||status.availability==='stale')&&<p>Сведения о ходе загрузки устарели. Это не подтверждает остановку процесса.</p>}
      {['missing','invalid','unreadable'].includes(status.availability)&&<p>Подтверждённые сведения о ходе загрузки пока недоступны.</p>}
      {status.error?.message&&<div className="workspace-alert" role="alert">{status.error.message}</div>}
      {status.company_binding_required&&<p id="general-source-unavailable">Привязка интеграции к точному ID компании Флордвор на сервере ещё не настроена.</p>}
      {!status.verified_snapshot_available&&<p>Проверенный снимок ещё не опубликован. Финансовые показатели появятся после завершения проверки и привязки компании.</p>}
      {!status.company_binding_required&&status.initial_loading&&<p id="general-source-unavailable">Первоначальная загрузка ещё не завершена. Второй сбор не запускается.</p>}
      <p>Время получения состояния и время действий загрузки не являются подтверждённой границей данных.</p>
    </>}
    {error&&<div className="workspace-alert" role="alert">{error}</div>}
  </section>;
}
export default function GeneralDashboard({access}){
  const [sections,setSections]=useState(null),[selected,setSelected]=useState('');
  const [envelopes,setEnvelopes]=useState({}),[sectionErrors,setSectionErrors]=useState({});
  const [error,setError]=useState(''),[loading,setLoading]=useState(false),[revision,setRevision]=useState(0);
  const [initialStatus,setInitialStatus]=useState(null),[initialError,setInitialError]=useState('');
  const owner=access.role==='superadmin';
  const scope=JSON.stringify(access);
  useEffect(()=>{
    setInitialStatus(null);setInitialError('');
    if(!owner)return undefined;
    let live=true,pending=false;
    const load=async()=>{
      if(pending)return;
      pending=true;
      try{const {data:status}=await api.get('/tenant/general-dashboard/initial-load-status');if(live){setInitialStatus(status);setInitialError('');}}
      catch{if(live)setInitialError('Не удалось получить актуальное состояние первоначальной загрузки. Управление источником закрыто до подтверждения состояния.');}
      finally{pending=false;}
    };
    load();const timer=setInterval(load,15000);
    return()=>{live=false;clearInterval(timer);};
  },[owner,scope]);
  const canReadSections=!owner||Boolean(initialStatus&&!initialStatus.company_binding_required&&initialStatus.verified_snapshot_available===true);
  const verifiedRevision=owner?initialStatus?.published_at||'verified':'permissions';
  useEffect(()=>{
    setSections(null);setEnvelopes({});setSectionErrors({});setError('');setLoading(false);
    if(!canReadSections)return undefined;
    let live=true;setLoading(true);
    (async()=>{
      try{
        const {data:catalog}=await api.get('/tenant/general-dashboard/sections');if(!live)return;
        setSections(catalog);const allowed=catalog.sections||[];
        const tabs=reportTabs(allowed);
        setSelected(previous=>tabs.some(section=>section.key===previous)?previous:tabs[0]?.key||'');
        const results=await Promise.all(allowed.map(async section=>{
          try{const response=await api.get(`/tenant/general-dashboard/section/${section.key}`);return [section.key,response.data,null];}
          catch(problem){return [section.key,null,errorText(problem)];}
        }));
        if(!live)return;
        setEnvelopes(Object.fromEntries(results.filter(([,data])=>data).map(([key,data])=>[key,data])));
        setSectionErrors(Object.fromEntries(results.filter(([,data])=>!data).map(([key,,reason])=>[key,reason])));
      }catch(problem){if(live)setError(errorText(problem));}
      finally{if(live)setLoading(false);}
    })();
    return()=>{live=false;};
  },[canReadSections,scope,revision,verifiedRevision]);
  const [adminTools,setAdminTools]=useState(false);
  const sourceControlsBlocked=!initialStatus||!!initialError||initialStatus.company_binding_required||initialStatus.initial_loading||['queued','running','initial_loading'].includes(initialStatus.state);
  useEffect(()=>{if(sourceControlsBlocked)setAdminTools(false);},[sourceControlsBlocked]);
  const [mode,setMode]=useState('week'),[weekKey,setWeekKey]=useState(''),[monthKey,setMonthKey]=useState('');
  const weeks=useMemo(()=>mergePublishedPeriods(envelopes,selected,'weeks'),[envelopes,selected]);
  const months=useMemo(()=>mergePublishedPeriods(envelopes,selected,'periods'),[envelopes,selected]);
  const visibleMode=mode==='week'?(weeks.length?'week':'month'):(months.length?'month':'week');
  const periods=visibleMode==='week'?weeks:months,key=visibleMode==='week'?weekKey:monthKey;
  const selectPeriod=visibleMode==='week'?setWeekKey:setMonthKey;
  const row=periods.find(period=>period.key===key)||periods[0];
  const data=envelopes[selected];
  const freshnessSections=selected==='receipts'?['receipts','writeoffs']:selected==='overview'?['overview','profitability']:[selected];
  const sectionFreshness=[...new Map(freshnessSections.flatMap(section=>generation(envelopes[section])===generation(data)?envelopes[section]?.section_rebuilds||[]:[])
    .map(entry=>[`${entry.month}:${entry.family}`,entry])).values()];
  const permissions=Object.keys(envelopes).filter(section=>generation(envelopes[section])===generation(data));
  const partial=unfinished(row,visibleMode);
  const year=/^\d{4}/.test(row?.key||'')?Number(row.key.slice(0,4)):null;
  const mismatched=data&&Object.values(envelopes).some(entry=>generation(entry)!==generation(data));
  const company=sections?.company?.name||initialStatus?.company?.name||'Панель компании';
  const tabs=reportTabs(sections?.sections||[]);
  const changeTabWithKeyboard=(event,index)=>{
    const movement={ArrowRight:1,ArrowLeft:-1,Home:'first',End:'last'}[event.key];
    if(movement===undefined||!tabs.length)return;
    event.preventDefault();
    const next=movement==='first'?0:movement==='last'?tabs.length-1:(index+movement+tabs.length)%tabs.length;
    setSelected(tabs[next].key);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next]?.focus();
  };
  return <section className="general-dashboard" aria-label="Дашборд Общая">
    <header className="topbar"><div className="brand"><span className="brand-mark" aria-hidden="true">ф</span><div><h1>Дашборд Общая</h1><span>{company} · Торговля и склад</span></div></div>
      <div className="header-right"><button type="button" className="refresh" disabled={!canReadSections||loading} onClick={()=>setRevision(value=>value+1)}>Обновить панель</button>
        {owner&&<button type="button" className="refresh" disabled={sourceControlsBlocked} onClick={()=>setAdminTools(value=>!value)}>{adminTools?'Закрыть управление источником':'Управление источником'}</button>}</div>
    </header>
    <div className="report-main">
      {owner&&<InitialLoadStatus status={initialStatus} error={initialError}/>}
      {owner&&!sourceControlsBlocked&&<DashboardRebuildControls kind="general" onComplete={()=>setRevision(value=>value+1)}/>}
      {error&&<div className="alert" role="alert">{error}</div>}
      {adminTools&&!sourceControlsBlocked?<FlordvorDashboard/>:<>
        {canReadSections&&<>
          <div className="page-heading"><div><div className="eyebrow">Контроль бизнеса</div><h2>{visibleMode==='week'?'Недельная сводка':'Месячная сводка'}</h2></div>
            {(weeks.length>0||months.length>0)&&<div className="period-tools"><div className="period-mode" role="group" aria-label="Период отчёта">
              <button type="button" aria-pressed={visibleMode==='week'} disabled={!weeks.length} onClick={()=>setMode('week')}>По неделям</button>
              <button type="button" aria-pressed={visibleMode==='month'} disabled={!months.length} onClick={()=>setMode('month')}>По месяцам</button>
            </div><label className="period-control">{visibleMode==='week'?'Неделя':'Месяц'}<select aria-label={visibleMode==='week'?'Неделя':'Месяц'} value={row?.key||''} onChange={event=>selectPeriod(event.target.value)}>{periods.map(period=><option key={period.key} value={period.key}>{period.label||period.key}</option>)}</select></label></div>}
          </div>
          {data&&<div className="source-strip">{sectionFreshness.length?'Базовый снимок на':'Данные на'}: {sourceTime(data.source_fact_at)} · Публикация: {sourceTime(data.published_at)}</div>}
          {data&&visibleMode==='month'&&<SectionRebuildFreshness rows={sectionFreshness} month={row?.key}/>}
          {visibleMode==='month'&&sectionFreshness.some(entry=>entry.month===row?.key)&&<p className="method-note">Границы ниже относятся к базовому снимку. Время отдельно пересобранных разделов указано выше; остальные показатели сохраняют прежнюю дату.</p>}
          {row&&<><div className="comparison"><span>{visibleMode==='week'&&row.isoWeek?`ISO-неделя ${row.isoWeek} · `:''}{periodRange(row.calendarFrom||row.from,row.calendarTo||row.to)}</span>
            {row.previousFrom&&<span>{year?`${year-1}: `:'Прошлый период: '}{periodRange(row.previousCalendarFrom||row.previousFrom,row.previousCalendarTo||row.previousTo)}</span>}
            {partial&&<span className="pill">{visibleMode==='week'?'Неделя':'Месяц'} не завершена · данные по {calendarDate(row.to)}</span>}</div>
            {(partial||row.historyLimited)&&<p className="period-explanation">В расчёте: {periodRange(row.from,row.to)} и {periodRange(row.previousFrom,row.previousTo)}. {partial?'Текущий период — накопленные факты; прошлый год — полный аналогичный период. Проценты предварительные.':''} {row.historyLimited?'История первого периода ограничена исходными данными.':''}</p>}
          </>}
          <div className="section-tabs" role="tablist" aria-label="Разделы общей панели">{tabs.map((section,index)=><button type="button" role="tab" id={`general-tab-${section.key}`} aria-controls={`general-panel-${section.key}`} tabIndex={selected===section.key?0:-1} aria-selected={selected===section.key} key={section.key} onKeyDown={event=>changeTabWithKeyboard(event,index)} onClick={()=>setSelected(section.key)}>{section.label}</button>)}</div>
          {sections&&!sections.sections.length&&<p className="workspace-empty">Разделы общего дашборда не назначены.</p>}
          {loading&&<p className="subtle" role="status">Загружаем показатели…</p>}
          {sectionErrors[selected]&&<div className="alert" role="alert">{sectionErrors[selected]}</div>}
          {mismatched&&<div className="alert" role="alert">Разделы получены из разных версий снимка. Показатели этих версий не объединяются. Перечитайте панель.</div>}
          {data&&<div role="tabpanel" id={`general-panel-${selected}`} aria-labelledby={`general-tab-${selected}`}><FlordvorReport section={selected} period={row} periods={periods} mode={visibleMode} onSelectPeriod={selectPeriod} permissions={permissions} dynamics={permissions.includes('dynamics')?envelopes.dynamics?.data?.deliveryDynamics:null}/></div>}
          {data&&selected!=='methodology'&&permissions.includes('methodology')&&<details className="methodology"><summary>Как считаются показатели</summary><FlordvorReport section="methodology" period={row} mode={visibleMode} permissions={permissions}/></details>}
        </>}
      </>}
    </div>
  </section>;
}
